import { useEffect, useRef, useState } from 'react';
import { UserAvatar } from './UserAvatar';

/**
 * Celebration overlay shown to viewers on the watch screen when an
 * auction ends with a winner. Ports the "Kinetic Ribbon" design from
 * the auction-win-animations preview.
 *
 * Renders absolutely positioned over its parent (intended for the
 * `.bv-video` container). Background is transparent so the gradient
 * ribbons appear over the live stream.
 *
 * Plays a ~2.5s entrance, holds briefly, then fades out over 0.5s.
 * Clicking the overlay also dismisses it early.
 */

interface Props {
  username: string;
  avatarUrl: string | null;
  onComplete: () => void;
}

// Timing breakdown:
//   ~1500ms entrance (avatar + text slam-in)
//    200ms hold with everything fully visible
//    500ms fade out
const TOTAL_DURATION_MS = 2200;
const FADE_OUT_DURATION_MS = 500;

const Easing = {
  easeOutExpo: (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  easeOutBack: (t: number) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

function useTime() {
  const [time, setTime] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      setTime((now - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return time;
}

function RibbonBar({
  y,
  height,
  startTime,
  color,
  speed = 1,
  direction = 'right',
}: {
  y: string;
  height: string;
  startTime: number;
  color: string;
  speed?: number;
  direction?: 'left' | 'right';
}) {
  const t = useTime() - startTime;
  if (t < 0) return null;
  const p = clamp(t / (0.9 / speed), 0, 1);
  const ease = Easing.easeOutExpo(p);
  const x = direction === 'right' ? -100 + ease * 100 : 100 - ease * 100;

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: y,
        width: '100%',
        height,
        transform: `translateX(${x}%)`,
        background: color,
      }}
    />
  );
}

function SlamText({
  y,
  text,
  startTime,
  size,
  color = '#fff',
}: {
  y: string;
  text: string;
  startTime: number;
  size: number;
  color?: string;
}) {
  const t = useTime() - startTime;
  if (t < 0) return null;

  let scale: number, opacity: number, blur: number;
  if (t < 0.15) {
    const p = t / 0.15;
    scale = 1.8 - p * 0.8;
    opacity = p;
    blur = (1 - p) * 10;
  } else if (t < 0.25) {
    const p = (t - 0.15) / 0.1;
    scale = 1 - Math.sin(p * Math.PI) * 0.06;
    opacity = 1;
    blur = 0;
  } else {
    scale = 1;
    opacity = 1;
    blur = 0;
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: y,
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity,
        filter: `blur(${blur}px)`,
        fontFamily:
          '"BW Modelica", "Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: 800,
        fontSize: size,
        color,
        letterSpacing: '-0.04em',
        lineHeight: 1,
        whiteSpace: 'nowrap',
        textShadow:
          '0 4px 24px rgba(0,0,0,0.5), 0 0 1px rgba(0,0,0,0.4)',
      }}
    >
      {text}
    </div>
  );
}

function AvatarPop({
  y,
  size,
  username,
  avatar,
  startTime,
}: {
  y: string;
  size: number;
  username: string;
  avatar: string | null;
  startTime: number;
}) {
  const t = useTime() - startTime;
  if (t < 0) return null;

  let scale: number, opacity: number, blur: number;
  if (t < 0.18) {
    const p = t / 0.18;
    scale = 1.6 - p * 0.6;
    opacity = p;
    blur = (1 - p) * 8;
  } else if (t < 0.3) {
    const p = (t - 0.18) / 0.12;
    scale = 1 - Math.sin(p * Math.PI) * 0.05;
    opacity = 1;
    blur = 0;
  } else {
    scale = 1;
    opacity = 1;
    blur = 0;
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: y,
        width: size,
        height: size,
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity,
        filter: `blur(${blur}px)`,
        borderRadius: '50%',
        // Stack the brand outline + drop shadow on the wrapper so the
        // UserAvatar renders the actual profile photo (or generic icon
        // fallback per CLAUDE.md — never initials).
        boxShadow: '0 0 0 6px #fff, 0 0 0 14px #FF2D55, 0 20px 50px rgba(0,0,0,0.5)',
      }}
    >
      <UserAvatar
        src={avatar}
        size={size}
        alt={`${username}'s avatar`}
      />
    </div>
  );
}

export default function AuctionWinOverlay({ username, avatarUrl, onComplete }: Props) {
  const [fadingOut, setFadingOut] = useState(false);
  const completedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    const fadeTimer = setTimeout(
      () => setFadingOut(true),
      TOTAL_DURATION_MS - FADE_OUT_DURATION_MS
    );
    const completeTimer = setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, TOTAL_DURATION_MS);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  // Track container width so all element sizes scale with the video tile.
  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => {
      const w = containerRef.current?.clientWidth ?? 800;
      setContainerWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const dismiss = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  };

  // Sizes scale off the container (video tile) width, not the viewport.
  // Tuned so a ~900px-wide video gives roughly the design proportions.
  const avatarSize = Math.max(72, Math.min(160, Math.round(containerWidth * 0.14)));
  const usernameSize = Math.max(28, Math.min(80, Math.round(containerWidth * 0.07)));
  const taglineSize = Math.max(40, Math.min(110, Math.round(containerWidth * 0.10)));

  return (
    <div
      ref={containerRef}
      onClick={dismiss}
      style={{
        // Pinned to the top half of the video frame.
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '50%',
        zIndex: 50,
        background: 'transparent',
        overflow: 'hidden',
        cursor: 'pointer',
        opacity: fadingOut ? 0 : 1,
        transition: `opacity ${FADE_OUT_DURATION_MS}ms ease-out`,
      }}
    >
      {/* Angled gradient bars sweep in to frame the content.
          Skew is gentler than the design's -8deg because our overlay
          has a wider aspect ratio (top-half of video ~4:1 vs the
          original 16:9 stage); -8deg dropped the bottom bar's left
          edge below the overlay and clipped it. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: 'skewY(-4deg) translateY(-4%)',
        }}
      >
        <RibbonBar
          y="9%"
          height="11%"
          startTime={0.1}
          color="linear-gradient(90deg, #5F3DC4, #CD3C9C)"
        />
        <RibbonBar
          y="24%"
          height="6%"
          startTime={0.2}
          color="#e462de"
          direction="left"
          speed={1.2}
        />
        <RibbonBar
          y="76%"
          height="7%"
          startTime={0.3}
          color="linear-gradient(90deg, #CD3C9C, #e462de)"
          direction="left"
        />
        <RibbonBar
          y="87%"
          height="11%"
          startTime={0.4}
          color="#5F3DC4"
          speed={0.9}
        />
      </div>

      {/* Avatar above username */}
      <AvatarPop
        y="33%"
        size={avatarSize}
        username={username}
        avatar={avatarUrl}
        startTime={0.7}
      />

      {/* Username */}
      <SlamText
        y="53%"
        text={username}
        startTime={0.9}
        size={usernameSize}
      />

      {/* Tagline */}
      <SlamText
        y="70%"
        text="SNAGGED IT"
        startTime={1.2}
        size={taglineSize}
      />
    </div>
  );
}
